import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Drawer, IconButton, useMediaQuery, useTheme } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LibrarySidebar from './LibrarySidebar';
import './index.css';

const SIDEBAR_DEFAULT = 260;
const SIDEBAR_MIN = 200;
const SIDEBAR_MAX = 480;
const SIDEBAR_RAIL = 28;
const SIDEBAR_WIDTH_KEY = 'hashely-library-sidebar-width';
const SIDEBAR_COLLAPSED_KEY = 'hashely-library-sidebar-collapsed';
const CANVAS_SIDEBAR_OPEN_KEY = 'hashely-canvas-sidebar-open';

function readCanvasSidebarOpen() {
  try {
    return localStorage.getItem(CANVAS_SIDEBAR_OPEN_KEY) === '1';
  } catch {
    return false;
  }
}

function readStoredWidth() {
  const stored = Number(localStorage.getItem(SIDEBAR_WIDTH_KEY));
  if (!Number.isFinite(stored)) return SIDEBAR_DEFAULT;
  return Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, stored));
}

const LibraryShell = ({
  children,
  sidebarProps,
  profileBar,
  toolbar,
  flushContent = false,
  toggleSidebar = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(readStoredWidth);
  const [collapsed, setCollapsed] = useState(
    () => !toggleSidebar && localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1'
  );
  const [sidebarOpen, setSidebarOpen] = useState(readCanvasSidebarOpen);
  const isResizingRef = useRef(false);

  useEffect(() => {
    localStorage.setItem(SIDEBAR_WIDTH_KEY, String(sidebarWidth));
  }, [sidebarWidth]);

  useEffect(() => {
    if (toggleSidebar) return;
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? '1' : '0');
  }, [collapsed, toggleSidebar]);

  useEffect(() => {
    if (!toggleSidebar) return;
    try {
      localStorage.setItem(CANVAS_SIDEBAR_OPEN_KEY, sidebarOpen ? '1' : '0');
    } catch {
      // ignore storage errors
    }
  }, [sidebarOpen, toggleSidebar]);

  const toggleCanvasSidebar = useCallback(() => {
    setSidebarOpen(prev => !prev);
  }, []);

  const handleResizeStart = useCallback(event => {
    event.preventDefault();
    isResizingRef.current = true;
    document.body.classList.add('library-sidebar-resizing');

    const startX = event.clientX;
    const startWidth = sidebarWidth;

    const handleMouseMove = moveEvent => {
      if (!isResizingRef.current) return;
      const nextWidth = Math.min(
        SIDEBAR_MAX,
        Math.max(SIDEBAR_MIN, startWidth + (moveEvent.clientX - startX))
      );
      setSidebarWidth(nextWidth);
    };

    const handleMouseUp = () => {
      isResizingRef.current = false;
      document.body.classList.remove('library-sidebar-resizing');
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [sidebarWidth]);

  const toggleCollapsed = useCallback(() => {
    setCollapsed(prev => !prev);
  }, []);

  const sidebar = sidebarProps ? (
    <LibrarySidebar {...sidebarProps} />
  ) : null;

  const profileWithMenu = profileBar
    ? React.cloneElement(profileBar, {
        showMenuButton: isMobile,
        onOpenSidebar: () => setMobileOpen(true),
      })
    : null;

  const toolbarWithMenu =
    toolbar && !profileBar
      ? React.cloneElement(toolbar, {
          showMenuButton: isMobile,
          onOpenSidebar: () => setMobileOpen(true),
        })
      : toolbar;

  return (
    <div className="library-page">
      {profileWithMenu}
      <div className="library-body">
        {isMobile ? (
          <Drawer
            className="library-sidebar-drawer"
            variant="temporary"
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            ModalProps={{ keepMounted: true }}
            sx={{
              '& .MuiDrawer-paper': { width: sidebarWidth, boxSizing: 'border-box' },
            }}
          >
            {sidebar}
          </Drawer>
        ) : toggleSidebar ? (
          <div className="library-sidebar-slot">
            <aside
              className={`library-sidebar library-sidebar--overlay${sidebarOpen ? ' is-open' : ' library-sidebar--rail'}`}
              style={{ width: sidebarOpen ? sidebarWidth : SIDEBAR_RAIL }}
            >
              {sidebar}
              <button
                type="button"
                className="library-sidebar-rail-toggle"
                aria-expanded={sidebarOpen}
                aria-label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
                onClick={toggleCanvasSidebar}
              >
                {sidebarOpen
                  ? <ChevronLeftIcon fontSize="small" />
                  : <ChevronRightIcon fontSize="small" />}
              </button>
              {sidebarOpen ? (
                <div
                  className="library-sidebar-resizer library-sidebar-resizer--overlay"
                  role="separator"
                  aria-orientation="vertical"
                  aria-label="Resize sidebar"
                  onMouseDown={handleResizeStart}
                />
              ) : null}
            </aside>
          </div>
        ) : (
          <>
            <aside
              className={`library-sidebar${collapsed ? ' library-sidebar--collapsed' : ''}`}
              style={{ width: collapsed ? 0 : sidebarWidth }}
            >
              {sidebar}
            </aside>
            <div
              className={`library-sidebar-edge${collapsed ? ' library-sidebar-edge--collapsed' : ''}`}
            >
              {!collapsed ? (
                <div
                  className="library-sidebar-resizer"
                  role="separator"
                  aria-orientation="vertical"
                  aria-label="Resize sidebar"
                  onMouseDown={handleResizeStart}
                />
              ) : null}
              <IconButton
                className="library-sidebar-toggle"
                size="small"
                aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                onClick={toggleCollapsed}
              >
                {collapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
              </IconButton>
            </div>
          </>
        )}
        <main className="library-main">
          {toolbarWithMenu}
          <div className={`library-content${flushContent ? ' library-content-flush' : ''}`}>
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default LibraryShell;
